// AI Chat Dashboards API client.
//
// A separate small HTTP API from both the WS chat endpoint (AiChat.jsx
// opens that directly) and the knowledge admin CRUD API (aiChatApi.js) —
// mirrors that file's apiFetch shape exactly. See
// .kiro/steering/ai-chat-dashboard-tracker.md locked decision #8.

import { apiFetch } from '../../lib/apiFetch.js';
import { API_HOSTS } from '../../config.js';

const DASHBOARDS_API_BASE_URL = API_HOSTS.aiChatDashboards;

async function readErrorMessage(response, fallback) {
  try {
    const cloned = response.clone();
    const data = await cloned.json();
    if (data && data.error) return data.error;
  } catch { /* fall through to text */ }
  try {
    const text = await response.text();
    if (text) return text.slice(0, 300);
  } catch { /* ignore */ }
  return fallback || `HTTP ${response.status}`;
}

export const dashboardsApi = {
  // ── List (sidebar) ────────────────────────────────────────────────────
  async listDashboards() {
    const response = await apiFetch(`${DASHBOARDS_API_BASE_URL}/api/dashboards`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load dashboards: ${response.status}`));
    }
    return response.json(); // { owned: [...], shared_with_me: [...] }
  },

  // ── Single dashboard ──────────────────────────────────────────────────
  async getDashboard(dashboardId) {
    const response = await apiFetch(`${DASHBOARDS_API_BASE_URL}/api/dashboards/${dashboardId}`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load dashboard: ${response.status}`));
    }
    return response.json();
  },

  async createDashboard({ title, widgets, filters, source_session_id }) {
    const response = await apiFetch(`${DASHBOARDS_API_BASE_URL}/api/dashboards`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, widgets, filters, source_session_id }),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to save dashboard: ${response.status}`));
    }
    return response.json();
  },

  async updateDashboard(dashboardId, updates) {
    const response = await apiFetch(`${DASHBOARDS_API_BASE_URL}/api/dashboards/${dashboardId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to update dashboard: ${response.status}`));
    }
    return response.json();
  },

  async deleteDashboard(dashboardId) {
    const response = await apiFetch(`${DASHBOARDS_API_BASE_URL}/api/dashboards/${dashboardId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to delete dashboard: ${response.status}`));
    }
    return response.json();
  },

  // ── Refresh (re-runs every widget's SQL with bound filter params) ────
  async refreshDashboard(dashboardId, filters) {
    const response = await apiFetch(`${DASHBOARDS_API_BASE_URL}/api/dashboards/${dashboardId}/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filters || {}),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to refresh dashboard: ${response.status}`));
    }
    return response.json();
  },

  // ── Sharing ────────────────────────────────────────────────────────────
  async shareDashboard(dashboardId, username) {
    const response = await apiFetch(`${DASHBOARDS_API_BASE_URL}/api/dashboards/${dashboardId}/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to share dashboard: ${response.status}`));
    }
    return response.json();
  },

  async revokeShare(dashboardId, username) {
    const response = await apiFetch(`${DASHBOARDS_API_BASE_URL}/api/dashboards/${dashboardId}/share/${encodeURIComponent(username)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to revoke share: ${response.status}`));
    }
    return response.json();
  },

  async listShares(dashboardId) {
    const response = await apiFetch(`${DASHBOARDS_API_BASE_URL}/api/dashboards/${dashboardId}/shares`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load shares: ${response.status}`));
    }
    return response.json();
  },

  async searchShareTargets(query) {
    const response = await apiFetch(`${DASHBOARDS_API_BASE_URL}/api/dashboards/share-targets?q=${encodeURIComponent(query)}`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `User search failed: ${response.status}`));
    }
    return response.json(); // { users: [{alias, email}] }
  },

  // ── Filter options ─────────────────────────────────────────────────────
  async getChannelOptions() {
    const response = await apiFetch(`${DASHBOARDS_API_BASE_URL}/api/dashboards/filter-options/channels`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Failed to load channels: ${response.status}`));
    }
    return response.json(); // { channels: [...] }
  },

  async searchStoreOptions(query) {
    const response = await apiFetch(`${DASHBOARDS_API_BASE_URL}/api/dashboards/filter-options/stores?q=${encodeURIComponent(query)}`);
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Store search failed: ${response.status}`));
    }
    return response.json(); // { stores: [{shop_id, shop_name}] }
  },
};
